import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertOctagon, AlertTriangle, ArrowRight, Calendar, Check, CheckCircle2, ChevronDown,
  ChevronRight, Home, Inbox, RouteOff, Search, X,
} from 'lucide-react';
import './MiscTaskApproval.css';
import FilterBar from '../../../components/FilterBar';
import WeekPickerNav from '../../../components/WeekPickerNav';
import config from '../../../configLoader';
import { useSiteContext } from '../../../SiteContext';
import { useSelectPayPeriod } from '../../../hooks/useSelectPayPeriod';
import { consumeStashedDeepLink, DeepLinkParams, peekStashedDeepLink } from '../../../interfaces/DeepLinkInterface';
import {
  MISC_TASK_STATUS, MiscTaskCounts, MiscTaskQueue, MiscTaskRow, MiscTaskViewer,
} from '../../../interfaces/MiscTaskApprovalInterface';
import {
  approveMiscTask, correctMiscTaskDescription, denyMiscTask, forceApproveMiscTask,
  getMiscTaskCounts, getMiscTaskErrorMessage, getMiscTaskErrorStatus, getMiscTaskHistory,
  getMiscTaskQueue,
} from '../../../api/miscTaskApproval';
import {
  BUCKETS, COLUMN_WIDTHS, emptyStateCopy, fullName, isPendingQueue, money,
  rejectionMessage, rowMatches, sortRows, stageSubline,
} from './miscTaskHelpers';
import MiscTaskRowView, { RowHistoryState } from './MiscTaskRow';
import MiscTaskDialogs, { MiscTaskDialogState } from './MiscTaskDialogs';

/**
 * NPM Reds — two-level approval for Miscellaneous Tasks (TAS-358).
 *
 * One page, four status buckets: 1st approval · 2nd approval · Approved ·
 * Denied. Identical for every user; only which rows land in a bucket and what
 * the action column offers change. The buckets ARE the status, so the table has
 * no status column and no status filter.
 *
 * Three deliberate departures from the prototype in the design bundle:
 *   D19  no bulk approve at either level — no checkboxes, no select-all;
 *   D21  no Remind button — it was a toast-only stub with no endpoint;
 *   D22  the optional approver comment is back at both levels, collapsed behind
 *        an affordance so it reads optional rather than skipped-required.
 *
 * The pay period comes from the shell's global week pill via SiteContext; this
 * page renders no picker of its own. (GpsExceptionsPage and SelfieExceptionsPage
 * both render a second one — don't copy them.)
 *
 * Every count on the page comes from GET /counts. Scoping is server-side, so
 * "I can see fewer rows than exist" is the normal case and no number here may be
 * recomputed from the loaded rows.
 */

const EMPTY_COUNTS: MiscTaskCounts = {
  firstLevel: { yours: 0, period: 0, dollars: 0 },
  secondLevel: { yours: 0, period: 0, dollars: 0 },
  approved: { yours: 0, period: 0, dollars: 0 },
  denied: { yours: 0, period: 0, dollars: 0 },
  unroutable: 0,
};

/**
 * Above this the Approved bucket stops pre-filling its "Approved by" column, so
 * a large period can't fan out into hundreds of history requests. Those rows
 * fall back to the row's own lastAction until they are expanded.
 */
const APPROVED_PREFETCH_LIMIT = 150;
const PREFETCH_CONCURRENCY = 6;

interface ToastItem { id: number; message: string; bad: boolean; }
type RowError = { headline: string; body: string };

const formatRange = (start: Date | string, end: Date | string): string => {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
};

const MiscTaskApprovalPage: React.FC = () => {
  const { payPeriodID, payPeriods, createPopup, pendingDeepLink } = useSiteContext();
  const selectPayPeriod = useSelectPayPeriod();

  const [queue, setQueue] = useState<MiscTaskQueue>('firstLevel');
  const [rows, setRows] = useState<MiscTaskRow[]>([]);
  const [viewer, setViewer] = useState<MiscTaskViewer | null>(null);
  const [counts, setCounts] = useState<MiscTaskCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** The API answered 404 on /counts — the two-level workflow is off on the server. */
  const [notEnabled, setNotEnabled] = useState(false);

  const [query, setQuery] = useState('');
  const [openRows, setOpenRows] = useState<Set<number>>(new Set());
  /** Per-row Vista drafts. Never persisted anywhere; cleared on a successful approve. */
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [comments, setComments] = useState<Record<number, string>>({});
  const [commentOpen, setCommentOpen] = useState<Set<number>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<number, RowError>>({});
  const [histories, setHistories] = useState<Record<number, RowHistoryState>>({});

  const [dialog, setDialog] = useState<MiscTaskDialogState | null>(null);
  const [busyRow, setBusyRow] = useState<number | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [flashID, setFlashID] = useState<number | null>(null);

  /** Ageing is relative to load time, so it doesn't tick under the reader. */
  const [now, setNow] = useState(() => new Date());
  const toastSeq = useRef(0);
  const requestSeq = useRef(0);
  /** Rows whose trail has been asked for. Doubles as the cache key — dropping an
   *  id here is what makes the next expand refetch it. */
  const inFlightHistory = useRef<Set<number>>(new Set());

  const ready = !!payPeriodID && payPeriodID !== 0;

  // ── toasts ─────────────────────────────────────────────────────────────────
  // No toast system exists in this app; this is the 5-line AdminManagementPage
  // pattern. SiteContext's createPopup is a BLOCKING modal and is reserved for
  // hard errors.
  const showToast = useCallback((message: string, bad = false) => {
    toastSeq.current += 1;
    const id = toastSeq.current;
    setToasts((prev) => [...prev, { id, message, bad }]);
    window.setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  }, []);

  const flashRow = useCallback((id: number) => {
    setFlashID(id);
    window.setTimeout(() => {
      document.getElementById(`mtb-row-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    window.setTimeout(() => setFlashID((cur) => (cur === id ? null : cur)), 1600);
  }, []);

  // ── loading ────────────────────────────────────────────────────────────────

  const loadCounts = useCallback(async () => {
    if (!ready) return;
    try {
      setCounts(await getMiscTaskCounts(payPeriodID));
      setNotEnabled(false);
    } catch (err) {
      // A 404 here is not a failure, it is a MISCONFIGURATION: the endpoint only
      // exists when the API has MISC_TASK_TWO_LEVEL_APPROVAL on, so this tenant's
      // page is switched on ahead of its server. Say so. Degrading to four zeroes
      // would be indistinguishable from a genuinely clean period — the exact
      // "looks like good news, actually nobody is watching" failure the empty-state
      // ladder exists to prevent.
      if (getMiscTaskErrorStatus(err) === 404) {
        setNotEnabled(true);
      } else {
        // Anything else: the cards degrade to zeroes rather than taking the page
        // down — the rows below them are the work.
        console.error('Misc task counts failed', err);
      }
    }
  }, [payPeriodID, ready]);

  const loadQueue = useCallback(async (target: MiscTaskQueue, spinner: boolean) => {
    if (!ready) return;
    requestSeq.current += 1;
    const seq = requestSeq.current;
    if (spinner) setLoading(true);
    setLoadError(null);
    try {
      const payload = await getMiscTaskQueue(payPeriodID, target);
      if (seq !== requestSeq.current) return; // a newer bucket won
      setViewer(payload.viewer);
      setRows(payload.rows ?? []);
      setNow(new Date());
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setRows([]);
      setLoadError(getMiscTaskErrorMessage(err, 'Miscellaneous tasks could not be loaded.'));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [payPeriodID, ready]);

  useEffect(() => { loadQueue(queue, true); }, [loadQueue, queue]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  // ── deep link ──────────────────────────────────────────────────────────────
  // The daily reds emails (TAS-544) link `?page=miscellaneousTasks&payPeriodID=N
  // &queue=firstLevel|secondLevel`. Without this the page opens on the current
  // week, which is usually not the one the approver was sent. Same two-channel
  // read as SelfieExceptionsPage: useAdminSession clears pendingDeepLink in the
  // effect that sets the page, and React batches those, so the stash covers the
  // case where the context value is already gone. The stash is only PEEKED until
  // this effect is done: usePayPeriods reads it too, to make the deep-linked
  // period the initial selection instead of the current week, and the fetch that
  // does so may resolve after this page has mounted. The pay-period switch below
  // is therefore normally a no-op and only matters when the list was already
  // loaded before arrival (in-app navigation with a stale stash).
  const deepLinkRef = useRef<DeepLinkParams | null | undefined>(undefined);
  useEffect(() => {
    if (deepLinkRef.current === null) return; // consumed
    if (deepLinkRef.current === undefined) {
      const fromContext = pendingDeepLink?.payPeriodID || pendingDeepLink?.queue ? pendingDeepLink : null;
      const link = fromContext ?? peekStashedDeepLink();
      deepLinkRef.current = link && (link.payPeriodID || link.queue) ? link : null;
      if (!deepLinkRef.current) return;
    }
    const link = deepLinkRef.current;
    if (link.payPeriodID && link.payPeriodID !== payPeriodID) {
      if (payPeriods.length === 0 || !payPeriodID) return; // shell still initialising — next pass
      const target = payPeriods.find((pp) => pp.PayPeriodID === link.payPeriodID);
      if (target) selectPayPeriod(target);
    }
    if (link.queue) setQueue(link.queue);
    deepLinkRef.current = null;
    consumeStashedDeepLink();
  }, [pendingDeepLink, payPeriods, payPeriodID, selectPayPeriod]);

  /** The period changed under us — everything on screen belonged to the old one. */
  useEffect(() => {
    setOpenRows(new Set());
    setDrafts({});
    setComments({});
    setCommentOpen(new Set());
    setRowErrors({});
    setHistories({});
    inFlightHistory.current.clear();
    setQuery('');
  }, [payPeriodID]);

  /** Drop a row's cached trail so the next expand refetches it. */
  const invalidateHistory = useCallback((id: number) => {
    inFlightHistory.current.delete(id);
    setHistories((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const fetchHistory = useCallback(async (id: number) => {
    // Guarded by a ref, not by inspecting state inside the updater: StrictMode
    // double-invokes updaters, and the prefetch loop can ask for the same row
    // twice before the first response lands.
    if (inFlightHistory.current.has(id)) return;
    inFlightHistory.current.add(id);
    setHistories((prev) => ({ ...prev, [id]: { loading: true, entries: null, error: null } }));
    try {
      const entries = await getMiscTaskHistory(id);
      setHistories((prev) => ({ ...prev, [id]: { loading: false, entries, error: null } }));
    } catch (err) {
      setHistories((prev) => ({
        ...prev,
        [id]: { loading: false, entries: null, error: getMiscTaskErrorMessage(err, 'The trail could not be loaded.') },
      }));
    }
  }, []);

  /**
   * The Approved bucket shows both approvers in the collapsed row, and only the
   * trail knows who they were — so it is pre-filled for that bucket alone, a few
   * requests at a time.
   */
  useEffect(() => {
    if (queue !== 'approved' || rows.length === 0 || rows.length > APPROVED_PREFETCH_LIMIT) return;
    let cancelled = false;
    const pending = rows.map((r) => r.miscEmployeeTaskID);
    (async () => {
      for (let i = 0; i < pending.length; i += PREFETCH_CONCURRENCY) {
        if (cancelled) return;
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(pending.slice(i, i + PREFETCH_CONCURRENCY).map((id) => fetchHistory(id)));
      }
    })();
    return () => { cancelled = true; };
  }, [queue, rows, fetchHistory]);

  // ── derived view ───────────────────────────────────────────────────────────

  const term = query.trim();
  const visible = useMemo(
    () => sortRows(rows.filter((r) => rowMatches(r, term)), queue),
    [rows, term, queue]
  );
  const bucketCount = counts[queue];
  const period = payPeriods.find((p) => p.PayPeriodID === payPeriodID);
  const anyOpen = visible.some((r) => openRows.has(r.miscEmployeeTaskID));

  /** The first unroutable red we can actually point at, if it is in this bucket. */
  const blockedHere = useMemo(
    () => rows.find((r) => r.approvalStatus < MISC_TASK_STATUS.approved && r.pendingApprovers.length === 0),
    [rows]
  );

  // ── interactions ───────────────────────────────────────────────────────────

  const toggleRow = useCallback((id: number) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    // Asking for a trail already loaded or in flight is a no-op, so this fires
    // unconditionally rather than doing I/O inside a state updater.
    fetchHistory(id);
  }, [fetchHistory]);

  const toggleAll = () => {
    if (anyOpen) { setOpenRows(new Set()); return; }
    const ids = visible.map((r) => r.miscEmployeeTaskID);
    setOpenRows(new Set(ids));
    ids.forEach((id) => fetchHistory(id));
  };

  const switchBucket = (next: MiscTaskQueue) => {
    if (next === queue) return;
    setQueue(next);
    setOpenRows(new Set());
  };

  const clearRowError = (id: number) =>
    setRowErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });

  /**
   * Somebody else got there first. Find the state the red is really in, move to
   * the bucket it landed in, flash it, and leave the reason in its action cell.
   *
   * There is no single-row read on the contract, so this asks all four buckets
   * at once. It is the rare path — correctness beats a saved request.
   */
  const recoverFromRejection = useCallback(async (
    row: MiscTaskRow,
    err: unknown,
    level: 1 | 2 | null
  ) => {
    const id = row.miscEmployeeTaskID;
    const status = getMiscTaskErrorStatus(err);
    const serverMessage = getMiscTaskErrorMessage(err, 'Someone else acted on this red first.');

    let landedIn: MiscTaskQueue | null = null;
    try {
      const all = await Promise.all(
        BUCKETS.map(async (b) => ({ queue: b.queue, payload: await getMiscTaskQueue(payPeriodID, b.queue) }))
      );
      const hit = all.find((r) => r.payload.rows?.some((x) => x.miscEmployeeTaskID === id));
      if (hit) {
        landedIn = hit.queue;
        setViewer(hit.payload.viewer);
        setQueue(hit.queue);
        setRows(hit.payload.rows);
        setNow(new Date());
      }
    } catch (refreshErr) {
      console.error('Could not refresh the rejected red', refreshErr);
    }

    setRowErrors((prev) => ({ ...prev, [id]: rejectionMessage(status, serverMessage, landedIn, level) }));
    invalidateHistory(id);
    flashRow(id);
    showToast(`Rejected · ${status ?? 'error'} — somebody else acted first`, true);
    loadCounts();
  }, [payPeriodID, flashRow, showToast, loadCounts, invalidateHistory]);

  /** Every mutation lands here: refresh the bucket and the cards, drop stale trail. */
  const afterMutation = useCallback(async (id: number) => {
    invalidateHistory(id);
    setOpenRows((prev) => { const next = new Set(prev); next.delete(id); return next; });
    await Promise.all([loadQueue(queue, false), loadCounts()]);
  }, [loadQueue, queue, loadCounts, invalidateHistory]);

  const runAction = async (
    row: MiscTaskRow,
    level: 1 | 2 | null,
    call: () => Promise<unknown>,
    onSuccess: () => void
  ) => {
    const id = row.miscEmployeeTaskID;
    clearRowError(id);
    setBusyRow(id);
    try {
      await call();
      onSuccess();
      await afterMutation(id);
    } catch (err) {
      const status = getMiscTaskErrorStatus(err);
      if (status === 403 || status === 409) {
        await recoverFromRejection(row, err, level);
      } else {
        // Not a designed state — a hard failure deserves the blocking modal.
        createPopup('The action did not go through', getMiscTaskErrorMessage(err, 'Please try again.'));
      }
    } finally {
      setBusyRow(null);
    }
  };

  const handleApprove = (row: MiscTaskRow, level: 1 | 2) => {
    const id = row.miscEmployeeTaskID;
    const description = (drafts[id] ?? '').trim();
    // A level-2 approve with no Vista text is refused server-side anyway
    // (SuperDescriptionRequired, D8) — this says so before the round trip. The
    // button used to be disabled instead, which left the reason unstated: a
    // greyed-out Approve with no message reads as broken, not as "type something".
    if (level === 2 && !description) {
      createPopup(
        'Vista description required',
        'Enter a Vista description before approving this red.'
      );
      return;
    }
    const comment = (comments[id] ?? '').trim();

    runAction(row, level, () => approveMiscTask(id, {
      level,
      ...(level === 2 ? { superDescription: description } : {}),
      ...(comment ? { comments: comment } : {}),
    }), () => {
      setDrafts((prev) => { const next = { ...prev }; delete next[id]; return next; });
      setComments((prev) => { const next = { ...prev }; delete next[id]; return next; });
      setCommentOpen((prev) => { const next = new Set(prev); next.delete(id); return next; });
      showToast(level === 1
        ? `Approved · ${fullName(row.name)} · ${money(row.amountClaimed)} — moved to 2nd approval`
        : `Approved · ${fullName(row.name)} · “${description}” goes to Vista`);
    });
  };

  const handleDeny = (row: MiscTaskRow, reason: string) => {
    const amount = money(row.originalAmountClaimed ?? row.amountClaimed);
    runAction(row, null, () => denyMiscTask(row.miscEmployeeTaskID, reason), () => {
      setDialog(null);
      showToast(`Denied · ${amount} off ${fullName(row.name)}'s pay — in the Denied bucket`, true);
    });
  };

  const handleForce = (row: MiscTaskRow, superDescription: string) => {
    runAction(row, null, () => forceApproveMiscTask(row.miscEmployeeTaskID, superDescription), () => {
      setDialog(null);
      setDrafts((prev) => { const next = { ...prev }; delete next[row.miscEmployeeTaskID]; return next; });
      showToast(`Force-approved · ${fullName(row.name)} — logged as a bypass`);
    });
  };

  const handleEditDescription = (row: MiscTaskRow, superDescription: string) => {
    runAction(row, null, () => correctMiscTaskDescription(row.miscEmployeeTaskID, superDescription), () => {
      setDialog(null);
      showToast('Description corrected — status unchanged');
    });
  };

  /** "Show it" on the no-approver line: find one, wherever it is. */
  const showFirstUnroutable = async () => {
    if (blockedHere) { setQuery(''); flashRow(blockedHere.miscEmployeeTaskID); return; }
    for (const target of ['firstLevel', 'secondLevel'] as MiscTaskQueue[]) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const payload = await getMiscTaskQueue(payPeriodID, target);
        const hit = payload.rows?.find(
          (r) => r.approvalStatus < MISC_TASK_STATUS.approved && r.pendingApprovers.length === 0
        );
        if (hit) {
          setViewer(payload.viewer);
          setQueue(target);
          setRows(payload.rows);
          setQuery('');
          setOpenRows(new Set());
          setNow(new Date());
          flashRow(hit.miscEmployeeTaskID);
          return;
        }
      } catch (err) {
        console.error('Could not locate the unroutable red', err);
      }
    }
    showToast('That red is no longer unroutable.', true);
  };

  // ── render ─────────────────────────────────────────────────────────────────

  if (!ready) {
    return (
      <div className="mtb">
        <div className="mtb-card">
          <div className="mtb-empty neutral">
            <span className="ei"><Calendar size={27} /></span>
            <h3>Pick a week</h3>
            <p>Reds are approved inside an open pay period. Choose one from the week selector above.</p>
          </div>
        </div>
      </div>
    );
  }

  const widths = COLUMN_WIDTHS[queue];
  const empty = emptyStateCopy(queue, !!viewer?.canOversee);
  const filteredToNothing = rows.length > 0 && visible.length === 0;

  // SUBMITTED, not Entry. MiscEmployeeTask.EntryDate is stamped week-by-week at
  // NPM — 2,489 of 2,490 reds in a live check sat exactly on the pay-period start,
  // and every period held exactly one distinct value — so the old ENTRY column was
  // structurally incapable of saying anything. The submit stamp does vary, and it
  // shows the late entry (5-6 days after the period closes) this page exists for.
  // The work date is not lost: it is still in the detail row, and it is still what
  // reaches Vista.
  const headers = (() => {
    if (queue === 'approved') {
      return ['', 'Employee', 'Job / phase', 'Submitted', 'Amount', 'What they did', 'Vista description', 'Approved by', ''];
    }
    if (queue === 'denied') {
      return ['', 'Employee', 'Job / phase', 'Submitted', 'Amount', 'Reason', 'Denied by', ''];
    }
    return ['', 'Employee', 'Job / phase', 'Submitted', 'Amount', 'What they did', 'Waiting on', 'Action'];
  })();

  return (
    <div className="mtb">
      <div className="mtb-crumb">
        <Home size={15} /> Home
        <ChevronRight size={15} />
        <b>Payroll</b>
        <ChevronRight size={15} />
        <span className="here">Miscellaneous Tasks</span>
      </div>

      <div className="mtb-head">
        <h1>
          Miscellaneous Tasks
          <span className="mtb-alias">Reds</span>
        </h1>
        {period && (
          <span className="mtb-period">
            <Calendar size={13} />
            Week {period.WeekNumber} · {formatRange(period.StartDate, period.EndDate)}
          </span>
        )}
        {/* ── REMOVE THIS WHEN NPM RETIRES THE v1 SHELL (TAS-100..120) ──────────
            The v2 shell renders the global week pill in TopBarV2, so this page
            normally owns no picker — that is the rule in the design handoff, and
            why it reads `payPeriodID` straight from context.

            But `version2` is false for npmechTest, and the v1 shell has no week
            control at all, so without this there is no way to leave the current
            period. Hence the `!config.version2` gate: it appears ONLY in the shell
            that lacks a pill, and can never double-render.

            GpsExceptionsPage and SelfieExceptionsPage render WeekPickerNav
            unconditionally (SelfieExceptionsPage.tsx:1483) and therefore show two
            pickers stacked in v2 — that is the bug the handoff warns about, not the
            picker itself. Do not copy their version. When NPM moves to the v2 shell,
            delete this block and the `.mtb-weeknav` rule; nothing else depends on it. */}
        {!config.version2 && payPeriods.length > 0 && (
          <div className="mtb-weeknav">
            <WeekPickerNav
              payPeriods={payPeriods}
              selectedIdx={payPeriods.findIndex((p) => p.PayPeriodID === payPeriodID)}
              onChange={(idx) => {
                const pp = payPeriods[idx];
                if (pp) selectPayPeriod(pp);
              }}
            />
          </div>
        )}
        {viewer && (
          <div className="mtb-who">
            Acting as <b>{viewer.name}</b>
            <span className="mtb-rolepill">{viewer.scopeLabel}</span>
          </div>
        )}
      </div>

      {/* The page is on for this tenant but the API is not. Loud, and above the
          stage cards, because every number below is meaningless in this state. */}
      {notEnabled && (
        <div className="mtb-blocked">
          <AlertOctagon size={16} />
          <div>
            <b>The two-level approval workflow is not enabled on this API.</b>{' '}
            The counts and buckets below are empty because the endpoints are switched
            off, not because there is nothing to approve. Set{' '}
            <code>MISC_TASK_TWO_LEVEL_APPROVAL</code> on the API and restart it.
          </div>
        </div>
      )}

      {/* Only ever driven by the server's `unroutable` count. The employee is
          named when the red happens to be in the bucket on screen; otherwise the
          sentence stays plural, because guessing a name would be worse. */}
      {!notEnabled && counts.unroutable > 0 && (
        <div className="mtb-blocked">
          <RouteOff size={16} />
          <div>
            <b>
              {counts.unroutable} red{counts.unroutable === 1 ? ' has' : 's have'} no approver.
            </b>{' '}
            {counts.unroutable === 1 ? (
              <>
                Nobody can approve it and{' '}
                {blockedHere ? blockedHere.name.first : 'that employee'} cannot submit a timesheet.
              </>
            ) : (
              <>Nobody can approve them and those employees cannot submit a timesheet.</>
            )}
          </div>
          <button type="button" onClick={showFirstUnroutable}>
            <ArrowRight size={13} />Show {counts.unroutable === 1 ? 'it' : 'them'}
          </button>
        </div>
      )}

      <div className="mtb-stages">
        {BUCKETS.map((b) => {
          const c = counts[b.queue];
          const oversees = !!viewer?.canOversee;
          const sub = stageSubline(b.queue, c.period, oversees);
          // The big number means different things to different viewers, per the design:
          // an APPROVER sees what is waiting on them (buckets 1-2) or what they acted on
          // (3-4) — that is `yours`. OVERSIGHT and PAYROLL are not in the chain at all, so
          // `yours` is structurally 0 for them and would render four dead zeroes above a
          // populated table; they get the size of their slice instead. The sub-line already
          // says which reading applies ("waiting on you" vs "in your slice").
          const headline = oversees ? c.period : c.yours;
          return (
            <button
              key={b.queue}
              type="button"
              className={`mtb-stage${queue === b.queue ? ' on' : ''}${headline === 0 ? ' zero' : ''}`}
              style={{ '--sc': b.color } as React.CSSProperties}
              onClick={() => switchBucket(b.queue)}
            >
              <span className="st-top"><span className="dot" />{b.label}</span>
              <span className="st-n">{headline}</span>
              <span className="st-sub">
                {sub.leadEmphasis ? <span className="st-you">{sub.lead}</span> : sub.lead}
                {sub.tail}
              </span>
              <span className="st-arrow"><ChevronRight size={13} /></span>
            </button>
          );
        })}
      </div>

      <div className="mtb-bar">
        <FilterBar
          value={query}
          onChange={setQuery}
          placeholder="Search employee, ID, job number, description…"
          label="Filter reds"
          matches={visible.length}
          total={rows.length}
          active={term.length > 0}
        />
        {visible.length > 0 && (
          <button type="button" className="mtb-expandall" onClick={toggleAll}>
            {anyOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {anyOpen ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>

      <div className="mtb-result">
        <span><b>{visible.length}</b> of <b>{rows.length}</b> shown{term ? ' (filtered)' : ''}</span>
        {isPendingQueue(queue) && (
          <>
            <span>· {money(bucketCount.dollars)} in this bucket</span>
            {/* D19: there is no bulk approve at either level. Saying so out loud
                stops the absence reading as an oversight. */}
            <span>· approved one at a time</span>
          </>
        )}
        <span className="scope">{viewer?.scopeLabel ?? '—'} · payPeriodID {payPeriodID}</span>
      </div>

      <div className="mtb-card">
        {loading && <div className="mtb-loading">Loading reds…</div>}

        {!loading && loadError && (
          <div className="mtb-empty danger">
            <span className="ei"><AlertTriangle size={27} /></span>
            <h3>Miscellaneous tasks could not be loaded</h3>
            <p>{loadError}</p>
            <div className="ea">
              <button type="button" className="mtb-btn" onClick={() => loadQueue(queue, true)}>Try again</button>
            </div>
          </div>
        )}

        {!loading && !loadError && rows.length === 0 && (
          <div className={`mtb-empty${isPendingQueue(queue) ? '' : ' neutral'}`}>
            <span className="ei">{isPendingQueue(queue) ? <CheckCircle2 size={27} /> : <Inbox size={27} />}</span>
            <h3>{empty.heading}</h3>
            <p>{empty.body}</p>
          </div>
        )}

        {!loading && !loadError && filteredToNothing && (
          <div className="mtb-empty neutral">
            <span className="ei"><Search size={27} /></span>
            <h3>Nothing matches</h3>
            <p>
              There {rows.length === 1 ? 'is' : 'are'} <b>{rows.length}</b> red{rows.length === 1 ? '' : 's'} in
              this bucket, the search is hiding {rows.length === 1 ? 'it' : 'them'}.
            </p>
            <div className="ea">
              <button type="button" className="mtb-btn" onClick={() => setQuery('')}>
                <X size={15} />Clear the search
              </button>
            </div>
          </div>
        )}

        {!loading && !loadError && visible.length > 0 && (
          <table>
            <colgroup>
              {widths.map((w, i) => <col key={i} style={{ width: w }} />)}
            </colgroup>
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th key={i} className={h === 'Amount' ? 'num' : undefined}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <MiscTaskRowView
                  key={row.miscEmployeeTaskID}
                  row={row}
                  viewer={viewer}
                  queue={queue}
                  term={term}
                  now={now}
                  open={openRows.has(row.miscEmployeeTaskID)}
                  flash={flashID === row.miscEmployeeTaskID}
                  history={histories[row.miscEmployeeTaskID]}
                  draft={drafts[row.miscEmployeeTaskID] ?? ''}
                  comment={comments[row.miscEmployeeTaskID] ?? ''}
                  commentOpen={commentOpen.has(row.miscEmployeeTaskID)}
                  error={rowErrors[row.miscEmployeeTaskID]}
                  busy={busyRow === row.miscEmployeeTaskID}
                  onToggle={toggleRow}
                  onDraftChange={(id, value) => setDrafts((p) => ({ ...p, [id]: value }))}
                  onCommentChange={(id, value) => setComments((p) => ({ ...p, [id]: value }))}
                  onCommentToggle={(id) => setCommentOpen((p) => {
                    const next = new Set(p);
                    if (next.has(id)) next.delete(id); else next.add(id);
                    return next;
                  })}
                  onApprove={handleApprove}
                  onDeny={(r) => setDialog({ kind: 'deny', row: r })}
                  onForce={(r) => setDialog({
                    kind: 'force', row: r, seedDescription: drafts[r.miscEmployeeTaskID] ?? '',
                  })}
                  onEditDescription={(r) => setDialog({ kind: 'edit', row: r })}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dialog && (
        <MiscTaskDialogs
          state={dialog}
          viewer={viewer}
          busy={busyRow === dialog.row.miscEmployeeTaskID}
          onClose={() => setDialog(null)}
          onDeny={handleDeny}
          onForce={handleForce}
          onEdit={handleEditDescription}
        />
      )}

      {toasts.length > 0 && createPortal(
        <div className="mtb-toasts">
          {toasts.map((t) => (
            <div key={t.id} className={`mtb-toast ${t.bad ? 'bad' : 'ok'}`}>
              {t.bad ? <AlertTriangle size={15} /> : <Check size={15} />}
              <span>{t.message}</span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default MiscTaskApprovalPage;
